import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { Table, SearchActions, Button, Badge, ConfirmDialog, storyColors, unwrapTrpcData } from '@org/ui';
import { trpc, RouterOutputs } from '@org/clients/school';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getMutationErrorMessage } from '../shared/multi-step-utils';

type WomanRow = RouterOutputs['women']['list'][number];

type WomenListProps = {
  onCreateWoman: () => void;
  onEditWoman: (woman: WomanRow) => void;
};

export const WomenList: React.FC<WomenListProps> = ({ onCreateWoman, onEditWoman }) => {
  const { t, i18n } = useTranslation('admin');
  const isPt = i18n.language?.startsWith('pt');
  const utils = trpc.useUtils();
  const { data } = trpc.women.list.useQuery();
  const [filterValue, setFilterValue] = React.useState('');
  const [pendingDelete, setPendingDelete] = React.useState<WomanRow | null>(null);
  const [womenData, setWomenData] = React.useState<WomanRow[]>([]);

  React.useEffect(() => {
    setWomenData(unwrapTrpcData<WomanRow[]>(data as WomanRow[]) ?? []);
  }, [data]);

  const deleteMutation = trpc.women.delete.useMutation({
    onSuccess: async () => {
      await utils.women.list.invalidate();
      toast.success(t('women.list.toasts.deleteSuccess'));
    },
    onError: (error: unknown) =>
      toast.error(t('women.list.toasts.deleteError', { message: getMutationErrorMessage(error) })),
  });

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate({ womanId: pendingDelete.womanId });
    setPendingDelete(null);
  };

  const columnHelper = createColumnHelper<WomanRow>();

  const columns = [
    columnHelper.display({
      id: 'portrait',
      header: t('women.list.columns.portrait'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div
            className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-border text-[10px] uppercase text-muted-foreground"
            style={{ backgroundColor: storyColors.portraitDisc }}
          >
            {row.portraitUrl ? (
              <img src={row.portraitUrl} alt="" className="h-full w-full object-cover object-top" />
            ) : (
              '—'
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.name || t('women.list.noName'), {
      id: 'name',
      header: t('women.list.columns.name'),
      cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    }),
    columnHelper.accessor((row) => row.years ?? '', {
      id: 'years',
      header: t('women.list.columns.years'),
    }),
    columnHelper.accessor((row) => (isPt ? row.fieldPt || row.field : row.field || row.fieldPt) ?? '', {
      id: 'field',
      header: t('women.list.columns.field'),
    }),
    columnHelper.accessor((row) => row.unitCount, {
      id: 'units',
      header: t('women.list.columns.units'),
    }),
    columnHelper.accessor(
      (row) => (row.storyStatus === 'published' ? t('women.list.status.published') : t('women.list.status.draft')),
      {
        id: 'story',
        header: t('women.list.columns.story'),
        cell: (info) => (
          <Badge variant={info.row.original.storyStatus === 'published' ? 'outline' : 'secondary'}>
            {info.getValue()}
          </Badge>
        ),
      },
    ),
    columnHelper.accessor((row) => row.storyCards?.length ?? 0, {
      id: 'cards',
      header: t('women.list.columns.cards'),
    }),
    columnHelper.display({
      id: 'edit',
      header: t('women.list.columns.actions'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onEditWoman(row)}>
              {t('women.list.actions.edit')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-red-600"
              disabled={deleteMutation.isPending}
              onClick={(event) => {
                event.stopPropagation();
                setPendingDelete(row);
              }}
            >
              {t('women.list.actions.delete')}
            </Button>
          </div>
        );
      },
    }),
  ];

  return (
    <div>
      <div>
        <SearchActions onClickAdd={onCreateWoman} onFilterChange={setFilterValue} />
      </div>
      <div>
        <Table
          tableData={womenData as any}
          columns={columns}
          getRowId={(row: any) => row.womanId}
          globalFilter={filterValue}
        />
      </div>
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        description={
          pendingDelete && pendingDelete.unitCount > 0
            ? t('women.list.deleteConfirmUnits', {
                name: pendingDelete.name,
                count: pendingDelete.unitCount,
              })
            : t('women.list.deleteConfirm', { name: pendingDelete?.name ?? '' })
        }
        confirmLabel={t('women.list.actions.delete')}
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
};
